import { BookOpen, Gamepad2, MessageCircle, Search, Send, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useShop } from '../../store';
import type { Product } from '../../types';
import AssistantCanvasGames, { type AssistantArcadeGame, type AssistantDifficulty } from './AssistantCanvasGames';

type TabId = 'chat' | 'books' | 'games';
type ChatRole = 'bot' | 'user';

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
  createdAt: string;
};

type GameId = 'quiz' | 'scramble' | 'memory' | 'flappy' | 'block' | 'dino' | 'snake';
type Difficulty = AssistantDifficulty;

const QUIZ_QUESTIONS = [
  {
    q: "Who wrote 'Things Fall Apart'?",
    options: ['Wole Soyinka', 'Chinua Achebe', "Ngugi wa Thiong'o", 'Ben Okri'],
    answerIndex: 1,
  },
  {
    q: "'Atomic Habits' was written by?",
    options: ['Malcolm Gladwell', 'Cal Newport', 'James Clear', 'Ryan Holiday'],
    answerIndex: 2,
  },
  {
    q: "What genre is '1984' by George Orwell?",
    options: ['Romance', 'Dystopian Fiction', 'Historical', 'Sci-Fi'],
    answerIndex: 1,
  },
  {
    q: "'The Alchemist' was originally written in?",
    options: ['Spanish', 'Italian', 'French', 'Portuguese'],
    answerIndex: 3,
  },
  {
    q: 'What currency does Haapa Store use?',
    options: ['KES', 'USD', 'RWF', 'GBP'],
    answerIndex: 2,
  },
  {
    q: "Who wrote 'Purple Hibiscus'?",
    options: ['Chimamanda Adichie', 'Chinua Achebe', 'Ngugi wa Thiong\'o', 'Nadine Gordimer'],
    answerIndex: 0,
  },
  {
    q: "Who wrote 'Sapiens'?",
    options: ['Yuval Noah Harari', 'Peter Thiel', 'Morgan Housel', 'James Clear'],
    answerIndex: 0,
  },
  {
    q: "What is the main theme of 'Rich Dad Poor Dad'?",
    options: ['Cooking', 'Building wealth', 'Space travel', 'Music theory'],
    answerIndex: 1,
  },
  {
    q: 'Which section shows your purchased content?',
    options: ['Wishlist', 'My Library', 'Contact', 'Publishers'],
    answerIndex: 1,
  },
  {
    q: 'Where do admin replies appear inside the site?',
    options: ['Header notifications', 'Footer only', 'Checkout only', 'About page'],
    answerIndex: 0,
  },
  {
    q: 'What is the primary action in Checkout?',
    options: ['Pay & confirm order', 'Delete account', 'Upload a book', 'Create a vendor'],
    answerIndex: 0,
  },
  {
    q: 'Which media types does Haapa Store support?',
    options: ['Books only', 'Books, audio, animations', 'Cars', 'Clothing'],
    answerIndex: 1,
  },
] as const;

const SCRAMBLE_WORDS = [
  { word: 'LIBRARY', hint: 'A place full of books' },
  { word: 'FICTION', hint: 'Imaginary stories' },
  { word: 'AUTHOR', hint: 'Who writes the book' },
  { word: 'BOOKMARK', hint: 'Marks your reading spot' },
  { word: 'DELIVERY', hint: 'Getting your order to you' },
  { word: 'CATALOG', hint: 'List of available books' },
  { word: 'CHECKOUT', hint: 'The page where you pay' },
  { word: 'PUBLISHER', hint: 'A company that releases books' },
  { word: 'ANIMATION', hint: 'Moving pictures / cartoons' },
  { word: 'NOTIFY', hint: 'Send a message to someone' },
] as const;

const MEMORY_TOKENS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'] as const;

const shuffle = <T,>(items: T[]): T[] => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const otherIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[otherIndex]] = [result[otherIndex], result[index]];
  }
  return result;
};

const scrambleWord = (word: string): string => {
  const letters = word.split('');
  let scrambled = word;
  for (let attempt = 0; attempt < 10 && scrambled === word; attempt += 1) {
    scrambled = shuffle(letters).join('');
  }
  return scrambled;
};

const formatTime = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return '';
  }
};

const normalizeText = (value: string): string => value.toLowerCase().replace(/\s+/g, ' ').trim();

const buildId = (): string => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const resolveProductLabel = (product: Product, t: (key: string) => string): string => {
  const label = t(product.nameKey || '').trim();
  if (label && label !== product.nameKey) return label;
  return product.authorName?.trim() || product.slug?.trim() || 'Book';
};

const resolveProductAuthor = (product: Product): string => `${product.authorName ?? product.author ?? ''}`.trim();

const resolveProductImage = (product: Product): string => `${product.coverUrl ?? product.imageUrl ?? ''}`.trim();

const resolveProductSummary = (product: Product, t: (key: string) => string): string => {
  const translated = t(product.descriptionKey || '').trim();
  if (translated && translated !== product.descriptionKey) return translated;
  return `${product.summary ?? ''}`.trim();
};

const buildFaq = (t: (key: string, options?: any) => string) => ({
  'how does haapa work': t('assistant.faq.howWorks', {
    defaultValue:
      'Haapa Store lets you browse books, add to cart, pay securely, and we deliver. You can also enjoy audio and animations.',
  }),
  order: t('assistant.faq.order', {
    defaultValue: 'To order: browse -> add to cart -> checkout -> enter address -> choose payment -> confirm.',
  }),
  payment: t('assistant.faq.payment', {
    defaultValue: 'We support Mobile Money, cards, and Cash on Delivery depending on your region.',
  }),
  delivery: t('assistant.faq.delivery', {
    defaultValue: 'Delivery time depends on your location. After purchase you can track in your dashboard.',
  }),
  account: t('assistant.faq.account', {
    defaultValue: 'Create an account from the Login / Create Account page, then sign in to access your library and orders.',
  }),
  genres: t('assistant.faq.genres', {
    defaultValue: 'Tell me what you like (fiction, self-help, finance, African literature) and I will recommend books.',
  }),
  contact: t('assistant.faq.contact', {
    defaultValue: 'You can contact us from the Contact page. We reply as soon as possible.',
  }),
} as const);

const ChatBubbleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white" aria-hidden="true">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
  </svg>
);

const PanelTitle = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-orange-100 pb-3">
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-50 text-orange-700">
      <Icon className="h-5 w-5" />
    </span>
    <h3 className="text-base font-extrabold text-slate-900">{title}</h3>
  </div>
);

export default function HaapaAssistantWidget() {
  const { t, i18n } = useTranslation();
  const { products, addToCart } = useShop();

  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<TabId>('chat');
  const [hasAttention, setHasAttention] = useState(true);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: buildId(),
      role: 'bot',
      text: t('assistant.chat.welcome', {
        defaultValue:
          "Welcome to Haapa Store! I'm your assistant. Ask about books, orders, delivery, or try a game in the Games tab.",
      }),
      createdAt: new Date().toISOString(),
    },
  ]);

  const messagesRef = useRef<HTMLDivElement | null>(null);
  const books = useMemo(() => products.filter((product) => (product.mediaType ?? 'book') === 'book'), [products]);
  const [bookQuery, setBookQuery] = useState('');
  const [activeBook, setActiveBook] = useState<Product | null>(null);

  const [game, setGame] = useState<GameId>('quiz');
  const [difficulty, setDifficulty] = useState<Difficulty>('easy');
  const [fullscreenGame, setFullscreenGame] = useState<AssistantArcadeGame | null>(null);
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window === 'undefined' ? 800 : window.innerHeight));
  const previousGamesStateRef = useRef<{ game: GameId; difficulty: Difficulty } | null>(null);

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const quizTargetCount = difficulty === 'easy' ? 5 : difficulty === 'medium' ? 8 : 12;
  const scrambleTargetCount = difficulty === 'easy' ? 4 : difficulty === 'medium' ? 6 : 10;
  const memoryPairsCount = difficulty === 'easy' ? 6 : difficulty === 'medium' ? 8 : 10;

  const [quizQuestions, setQuizQuestions] = useState(() => shuffle([...QUIZ_QUESTIONS]).slice(0, 5));
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quizAnswered, setQuizAnswered] = useState(false);

  const [scrambleIndex, setScrambleIndex] = useState(0);
  const [scrambleScore, setScrambleScore] = useState(0);
  const [scrambledWord, setScrambledWord] = useState(() => scrambleWord(SCRAMBLE_WORDS[0].word));
  const [scrambleGuess, setScrambleGuess] = useState('');
  const [scrambleResult, setScrambleResult] = useState<'idle' | 'ok' | 'no'>('idle');

  const [memoryCards, setMemoryCards] = useState<string[]>(() => shuffle([...MEMORY_TOKENS.slice(0, 6), ...MEMORY_TOKENS.slice(0, 6)]));
  const [memoryFlipped, setMemoryFlipped] = useState<number[]>([]);
  const [memoryMatched, setMemoryMatched] = useState<number[]>([]);
  const [memoryMoves, setMemoryMoves] = useState(0);
  const [memorySeconds, setMemorySeconds] = useState(0);
  const [memoryLocked, setMemoryLocked] = useState(false);
  const memoryTimerRef = useRef<number | null>(null);
  const filteredBooks = useMemo(() => {
    const q = normalizeText(bookQuery);
    if (!q) return books;
    return books.filter((product) => {
      const label = normalizeText(resolveProductLabel(product, t));
      const author = normalizeText(resolveProductAuthor(product));
      const summary = normalizeText(resolveProductSummary(product, t));
      return label.includes(q) || author.includes(q) || summary.includes(q);
    });
  }, [bookQuery, books, t]);
  const unreadDot = hasAttention && !isOpen;

  const pushMessage = useCallback((role: ChatRole, text: string) => {
    setMessages((previous) => [...previous, { id: buildId(), role, text, createdAt: new Date().toISOString() }]);
  }, []);

  const getChatReply = useCallback(
    (question: string): string => {
      const q = normalizeText(question);
      const faq = buildFaq(t);
      const includesAny = (phrases: string[]) => phrases.some((phrase) => q.includes(normalizeText(phrase)));

      const language = (i18n.language || 'en').toLowerCase();
      const isFr = language.startsWith('fr');
      const isSw = language.startsWith('sw');
      const isRw = language.startsWith('rw');

      const isHowWorks = includesAny([
        'how does haapa work',
        'how it works',
        ...(isFr ? ['comment fonctionne haapa', 'comment ca marche', 'comment ca marche haapa'] : []),
        ...(isSw ? ['haapa inafanyaje kazi', 'inafanyaje kazi', 'haapa inavyofanya kazi'] : []),
        ...(isRw ? ['haapa ikora ite', 'uko haapa ikora', 'uko bikora'] : []),
      ]);
      if (isHowWorks) return faq['how does haapa work'];

      const isOrder = includesAny([
        'order',
        'buy',
        'checkout',
        'cart',
        ...(isFr ? ['commander', 'commande'] : []),
        ...(isSw ? ['agiza', 'oda', 'kuagiza'] : []),
        ...(isRw ? ['oda', 'tegeka', 'gutega', 'gutanga oda'] : []),
      ]);
      if (isOrder) return faq.order;

      const isPayment = includesAny([
        'payment',
        'pay',
        'mobile money',
        ...(isFr ? ['paiement', 'payer'] : []),
        ...(isSw ? ['malipo', 'kulipa'] : []),
        ...(isRw ? ['kwishyura', 'uburyo bwo kwishyura'] : []),
      ]);
      if (isPayment) return faq.payment;

      const isDelivery = includesAny([
        'delivery',
        'shipping',
        'ship',
        ...(isFr ? ['livraison'] : []),
        ...(isSw ? ['usafirishaji', 'delivery'] : []),
        ...(isRw ? ['delivery', 'gutanga', 'kugerezaho'] : []),
      ]);
      if (isDelivery) return faq.delivery;

      const isAccount = includesAny([
        'account',
        'sign up',
        'create account',
        ...(isFr ? ['compte', 'creer un compte'] : []),
        ...(isSw ? ['akaunti', 'jisajili'] : []),
        ...(isRw ? ['konti', 'kwiyandikisha'] : []),
      ]);
      if (isAccount) return faq.account;

      const isGenres = includesAny([
        'genre',
        'genres',
        ...(isFr ? ['genres'] : []),
        ...(isSw ? ['aina', 'aina za vitabu'] : []),
        ...(isRw ? ['ubwoko', "ubwoko bw'ibitabo"] : []),
      ]);
      if (isGenres) return faq.genres;

      const isContact = includesAny([
        'contact',
        ...(isFr ? ['contacter', 'contact'] : []),
        ...(isSw ? ['wasiliana', 'contact'] : []),
        ...(isRw ? ['twandikire', 'contact'] : []),
      ]);
      if (isContact) return faq.contact;

      const found = books.find((product) => {
        const label = normalizeText(resolveProductLabel(product, t));
        const author = normalizeText(resolveProductAuthor(product));
        const summary = normalizeText(resolveProductSummary(product, t));
        return label.includes(q) || author.includes(q) || summary.includes(q);
      });

      if (found) {
        const label = resolveProductLabel(found, t);
        const author = resolveProductAuthor(found);
        const price = `${found.currency ?? 'RWF'} ${new Intl.NumberFormat('en-US').format(found.price)}`;
        return `I found "${label}"${author ? ` by ${author}` : ''}. Price: ${price}. Open the Books tab to view and add to cart.`;
      }

      if (q.match(/^(hi|hello|hey|good\\s)/))
        return t('assistant.chat.greeting', { defaultValue: 'Hello! How can I help you today?' });
      if (q.includes('thank'))
        return t('assistant.chat.thanks', { defaultValue: "You're welcome! Happy reading." });
      if (q.match(/price|cost|how much/))
        return t('assistant.chat.priceHelp', { defaultValue: 'Open the Books tab to see exact prices for each title.' });
      if (q.match(/recommend|suggest/))
        return t('assistant.chat.recommendHelp', {
          defaultValue: 'Tell me a genre you enjoy (fiction, self-help, finance, African literature).',
        });

      const fallback = [
        t('assistant.chat.fallback1', { defaultValue: 'Try asking about ordering, delivery, payment, books, or account.' }),
        t('assistant.chat.fallback2', { defaultValue: 'I can recommend books and help you navigate the store.' }),
        t('assistant.chat.fallback3', { defaultValue: 'Open the Books tab to browse titles, or try a game in Games.' }),
      ];
      return fallback[Math.floor(Math.random() * fallback.length)];
    },
    [books, i18n.language, t],
  );

  const sendMessage = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    pushMessage('user', text);
    setDraft('');
    setHasAttention(false);
    window.setTimeout(() => pushMessage('bot', getChatReply(text)), 450);
  }, [draft, getChatReply, pushMessage]);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, tab, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setHasAttention(false);
  }, [isOpen]);

  useEffect(() => {
    const shouldRun = isOpen && tab === 'games' && game === 'memory' && memoryMatched.length !== memoryCards.length;
    if (!shouldRun) {
      if (memoryTimerRef.current) {
        window.clearInterval(memoryTimerRef.current);
        memoryTimerRef.current = null;
      }
      return;
    }

    if (memoryTimerRef.current) return;
    memoryTimerRef.current = window.setInterval(() => {
      setMemorySeconds((previous) => previous + 1);
    }, 1000);

    return () => {
      if (memoryTimerRef.current) {
        window.clearInterval(memoryTimerRef.current);
        memoryTimerRef.current = null;
      }
    };
  }, [game, isOpen, memoryCards.length, memoryMatched.length, tab]);

  const quickPrompts = useMemo(
    () => [
      { label: t('assistant.chat.prompts.howWorks.label', { defaultValue: 'How it works' }), text: t('assistant.chat.prompts.howWorks.text', { defaultValue: 'How does Haapa work?' }) },
      { label: t('assistant.chat.prompts.order.label', { defaultValue: 'Order' }), text: t('assistant.chat.prompts.order.text', { defaultValue: 'How do I order a book?' }) },
      { label: t('assistant.chat.prompts.payment.label', { defaultValue: 'Payment' }), text: t('assistant.chat.prompts.payment.text', { defaultValue: 'What payment methods do you accept?' }) },
      { label: t('assistant.chat.prompts.delivery.label', { defaultValue: 'Delivery' }), text: t('assistant.chat.prompts.delivery.text', { defaultValue: 'Do you offer delivery?' }) },
      { label: t('assistant.chat.prompts.genres.label', { defaultValue: 'Genres' }), text: t('assistant.chat.prompts.genres.text', { defaultValue: 'Tell me about popular genres' }) },
      { label: t('assistant.chat.prompts.account.label', { defaultValue: 'Account' }), text: t('assistant.chat.prompts.account.text', { defaultValue: 'How do I create an account?' }) },
    ],
    [t],
  );

  const handleAddToCart = (product: Product) => {
    addToCart(product, 1);
    setActiveBook(null);
    setTab('chat');
    pushMessage('bot', `Added "${resolveProductLabel(product, t)}" to your cart. You can checkout when ready.`);
  };

  const quizQuestion = quizQuestions[quizIndex] ?? null;
  const isQuizDone = quizIndex >= quizQuestions.length;

  const answerQuiz = (optionIndex: number) => {
    if (!quizQuestion || quizAnswered) return;
    setQuizAnswered(true);
    const isCorrect = optionIndex === quizQuestion.answerIndex;
    window.setTimeout(() => {
      setQuizScore((previous) => previous + (isCorrect ? 1 : 0));
      setQuizIndex((previous) => previous + 1);
      setQuizAnswered(false);
    }, 650);
  };

  const resetQuiz = useCallback(() => {
    setQuizQuestions(shuffle([...QUIZ_QUESTIONS]).slice(0, quizTargetCount));
    setQuizIndex(0);
    setQuizScore(0);
    setQuizAnswered(false);
  }, [quizTargetCount]);

  const scrambleWords = useMemo(() => SCRAMBLE_WORDS.slice(0, scrambleTargetCount), [scrambleTargetCount]);
  const scrambleEntry = scrambleWords[scrambleIndex] ?? null;
  const isScrambleDone = scrambleIndex >= scrambleWords.length;

  const checkScramble = () => {
    if (!scrambleEntry) return;
    const guess = normalizeText(scrambleGuess).toUpperCase();
    if (!guess) return;
    if (guess === scrambleEntry.word) {
      setScrambleResult('ok');
      window.setTimeout(() => {
        const nextIndex = scrambleIndex + 1;
        const next = scrambleWords[nextIndex];
        setScrambleIndex(nextIndex);
        setScrambleScore((previous) => previous + 1);
        if (next) setScrambledWord(scrambleWord(next.word));
        setScrambleGuess('');
        setScrambleResult('idle');
      }, 650);
    } else {
      setScrambleResult('no');
    }
  };

  const skipScramble = () => {
    const nextIndex = scrambleIndex + 1;
    const next = scrambleWords[nextIndex];
    setScrambleIndex(nextIndex);
    if (next) setScrambledWord(scrambleWord(next.word));
    setScrambleGuess('');
    setScrambleResult('idle');
  };

  const resetScramble = useCallback(() => {
    setScrambleIndex(0);
    setScrambleScore(0);
    setScrambledWord(scrambleWord(scrambleWords[0]?.word ?? SCRAMBLE_WORDS[0].word));
    setScrambleGuess('');
    setScrambleResult('idle');
  }, [scrambleWords]);

  const resetMemory = useCallback(() => {
    const tokens = MEMORY_TOKENS.slice(0, memoryPairsCount);
    setMemoryCards(shuffle([...tokens, ...tokens]));
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setMemoryMoves(0);
    setMemorySeconds(0);
    setMemoryLocked(false);
  }, [memoryPairsCount]);

  const flipMemory = (index: number) => {
    if (memoryLocked) return;
    if (memoryMatched.includes(index)) return;
    if (memoryFlipped.includes(index)) return;
    if (memoryFlipped.length >= 2) return;

    const nextFlipped = [...memoryFlipped, index];
    setMemoryFlipped(nextFlipped);

    if (nextFlipped.length < 2) return;

    setMemoryMoves((previous) => previous + 1);
    const [a, b] = nextFlipped;
    const isMatch = memoryCards[a] === memoryCards[b];

    if (isMatch) {
      setMemoryMatched((previous) => [...previous, a, b]);
      setMemoryFlipped([]);
      return;
    }

    setMemoryLocked(true);
    window.setTimeout(() => {
      setMemoryFlipped([]);
      setMemoryLocked(false);
    }, 750);
  };

  useEffect(() => {
    if (tab !== 'games') {
      setFullscreenGame(null);
    }
  }, [tab]);

  useEffect(() => {
    if (!fullscreenGame) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreenGame]);

  useEffect(() => {
    if (!fullscreenGame) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFullscreenGame(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fullscreenGame]);

  useEffect(() => {
    if (tab !== 'games') return;
    const previous = previousGamesStateRef.current;
    const changed = !previous || previous.game !== game || previous.difficulty !== difficulty;
    if (!changed) return;

    if (game === 'quiz') resetQuiz();
    if (game === 'scramble') resetScramble();
    if (game === 'memory') resetMemory();

    previousGamesStateRef.current = { game, difficulty };
  }, [difficulty, game, resetMemory, resetQuiz, resetScramble, tab]);

  return (
    <>
      {fullscreenGame ? (
        <div className="fixed inset-0 z-[10000] bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="mx-auto flex h-full w-full max-w-5xl flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-orange-200 bg-white/95 px-4 py-3 shadow-sm">
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-slate-900">
                  {fullscreenGame === 'flappy'
                    ? 'Flappy Book'
                    : fullscreenGame === 'block'
                      ? 'Block Jump'
                      : fullscreenGame === 'dino'
                        ? 'Dino Run'
                        : 'Snake'}{' '}
                  (Fullscreen)
                </p>
                <p className="text-xs font-semibold text-slate-500">Difficulty: {difficulty}</p>
              </div>
              <button
                type="button"
                onClick={() => setFullscreenGame(null)}
                className="inline-flex items-center justify-center rounded-2xl bg-orange-600 px-4 py-2 text-xs font-extrabold text-white transition hover:bg-orange-700"
              >
                {t('assistant.games.exit', { defaultValue: 'Exit' })}
              </button>
            </div>

            <div className="mt-4 flex-1">
              <AssistantCanvasGames
                game={fullscreenGame}
                difficulty={difficulty}
                showFullscreenButton={false}
                canvasHeight={Math.max(320, Math.min(viewportHeight - 210, 860))}
              />
              <p className="mt-3 text-center text-xs font-semibold text-white/80">Tip: Click/Tap or press Space to play. Press ESC to exit.</p>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="fixed bottom-24 right-4 z-[9999] grid h-14 w-14 place-items-center rounded-full border-2 border-white/30 bg-gradient-to-br from-orange-600 to-orange-500 shadow-[0_10px_28px_rgba(249,115,22,0.55)] transition hover:scale-110 hover:shadow-[0_16px_44px_rgba(249,115,22,0.7)] sm:bottom-6 sm:right-6"
        aria-label={t('assistant.openAria', { defaultValue: 'Open Haapa assistant' })}
      >
        {unreadDot ? (
          <span className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full border-2 border-orange-600 bg-white text-[10px] font-black text-orange-600">
            !
          </span>
        ) : null}
        <ChatBubbleIcon />
      </button>

      <section
        className={`fixed bottom-[152px] right-4 z-[9998] w-[92vw] max-w-[360px] overflow-hidden rounded-3xl border border-orange-200 bg-white shadow-[0_24px_62px_rgba(15,23,42,0.25)] transition sm:bottom-[88px] sm:right-6 ${
          isOpen ? 'scale-100 opacity-100' : 'pointer-events-none scale-90 opacity-0'
        }`}
        style={{ transformOrigin: 'bottom right' }}
        aria-hidden={!isOpen}
      >
        <header className="relative flex items-center gap-3 overflow-hidden bg-gradient-to-br from-slate-950 via-[#101d46] to-orange-800 px-4 py-3">
          <div className="grid h-11 w-11 place-items-center rounded-full border border-white/20 bg-white/10 text-sm font-extrabold text-white">HA</div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-extrabold tracking-wide text-white">
              {t('assistant.title', { defaultValue: 'Haapa Assistant' })}
            </p>
            <p className="text-xs font-semibold text-orange-100">
              <span className="mr-1 inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
              {t('assistant.status', { defaultValue: 'Online - Ready to help' })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label={t('assistant.closeAria', { defaultValue: 'Close assistant' })}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <nav className="flex border-b border-orange-100 bg-[#fff8f2]">
          {[
            { id: 'chat' as const, icon: MessageCircle, label: t('assistant.tabs.chat', { defaultValue: 'Chat' }) },
            { id: 'books' as const, icon: BookOpen, label: t('assistant.tabs.books', { defaultValue: 'Books' }) },
            { id: 'games' as const, icon: Gamepad2, label: t('assistant.tabs.games', { defaultValue: 'Games' }) },
          ].map((item) => {
            const Icon = item.icon;
            const active = tab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`flex flex-1 items-center justify-center gap-2 px-3 py-2 text-xs font-extrabold transition ${
                  active ? 'bg-white text-orange-600' : 'text-orange-700/70 hover:text-orange-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </button>
            );
          })}
        </nav>

        {tab === 'chat' ? (
          <div className="flex h-[480px] flex-col">
            <div ref={messagesRef} className="flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-[#fff8f0] to-white p-4">
              {messages.map((message) => (
                <div key={message.id} className={`flex max-w-[92%] gap-2 ${message.role === 'user' ? 'ml-auto flex-row-reverse' : ''}`}>
                  <div
                    className={`grid h-8 w-8 place-items-center rounded-full border text-xs font-bold ${
                      message.role === 'user'
                        ? 'border-orange-600 bg-orange-600 text-white'
                        : 'border-orange-200 bg-orange-50 text-orange-700'
                    }`}
                  >
                    {message.role === 'user' ? 'U' : 'AI'}
                  </div>
                  <div>
                    <div
                      className={`rounded-2xl px-4 py-2 text-sm leading-relaxed shadow-sm ${
                        message.role === 'user'
                          ? 'rounded-br-md bg-gradient-to-br from-orange-600 to-orange-500 text-white'
                          : 'rounded-bl-md border border-orange-100 bg-white text-slate-800'
                      }`}
                    >
                      {message.text}
                    </div>
                    <p className="mt-1 text-[10px] font-semibold text-slate-400">{formatTime(message.createdAt)}</p>
                  </div>
                </div>
              ))}
              <div className="pt-2 text-center text-[11px] font-semibold text-slate-400">
                {t('assistant.chat.needHuman', { defaultValue: 'Need human support?' })}{' '}
                <Link to="/contact" className="text-orange-700 hover:text-orange-600">
                  {t('assistant.chat.contactLink', { defaultValue: 'Contact us' })}
                </Link>
                .
              </div>
            </div>

            <div className="border-t border-orange-100 bg-white">
              <div className="flex flex-wrap gap-2 px-4 py-3">
                {quickPrompts.map((prompt) => (
                  <button
                    key={prompt.label}
                    type="button"
                    onClick={() => {
                      setDraft(prompt.text);
                      window.setTimeout(sendMessage, 0);
                    }}
                    className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-bold text-orange-700 transition hover:bg-orange-600 hover:text-white"
                  >
                    {prompt.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 px-3 pb-3">
                <input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') sendMessage();
                  }}
                  placeholder={t('assistant.chat.inputPlaceholder', { defaultValue: 'Ask me anything...' })}
                  aria-label={t('assistant.chat.inputAria', { defaultValue: 'Chat message' })}
                  className="h-10 flex-1 rounded-full border border-orange-200 bg-[#fff8f2] px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200/60"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-orange-600 to-orange-500 text-white shadow-sm transition hover:scale-105"
                  aria-label={t('assistant.chat.sendAria', { defaultValue: 'Send message' })}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          null
        )}

        {tab === 'books' ? (
          <div className="relative flex h-[480px] flex-col">
            <div className="p-4">
              <PanelTitle icon={BookOpen} title={t('assistant.books.title', { defaultValue: 'Book Catalog' })} />
              <label className="relative mt-3 block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={bookQuery}
                  onChange={(event) => setBookQuery(event.target.value)}
                  placeholder={t('assistant.books.searchPlaceholder', { defaultValue: 'Search books, authors...' })}
                  className="w-full rounded-full border border-orange-200 bg-[#fff8f2] py-2 pl-10 pr-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200/60"
                />
              </label>
              <p className="mt-2 text-xs font-semibold text-slate-500">
                {t('assistant.books.showing', {
                  defaultValue: 'Showing {{count}} book(s) from the store database.',
                  count: filteredBooks.length,
                })}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {filteredBooks.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-orange-200 bg-white p-8 text-center text-sm font-semibold text-slate-600">
                  {t('assistant.books.none', { defaultValue: 'No books match your search.' })}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredBooks.slice(0, 40).map((product) => {
                    const label = resolveProductLabel(product, t);
                    const author = resolveProductAuthor(product);
                    const image = resolveProductImage(product);
                    return (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => setActiveBook(product)}
                        className="flex w-full items-start gap-3 rounded-2xl border border-orange-100 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md"
                      >
                        {image ? (
                          <img src={image} alt={label} className="h-[72px] w-[54px] rounded-xl object-cover" loading="lazy" />
                        ) : (
                          <div className="grid h-[72px] w-[54px] place-items-center rounded-xl bg-gradient-to-br from-orange-100 to-orange-50 text-orange-700">
                            <BookOpen className="h-6 w-6" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-extrabold text-slate-900">{label}</p>
                          <p className="mt-0.5 truncate text-xs font-semibold text-slate-500">{author || '-'}</p>
                          <p className="mt-2 text-sm font-extrabold text-orange-600">
                            {(product.currency ?? 'RWF').toUpperCase()} {new Intl.NumberFormat('en-US').format(product.price)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {activeBook ? (
              <div className="absolute inset-0 z-20 grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
                <article className="w-full max-w-md rounded-3xl border border-orange-200 bg-white p-5 shadow-[0_28px_80px_rgba(15,23,42,0.35)]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-lg font-extrabold text-slate-900">{resolveProductLabel(activeBook, t)}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-500">{resolveProductAuthor(activeBook) || '-'}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveBook(null)}
                      className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                      aria-label="Close book details"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <p className="mt-3 text-sm leading-relaxed text-slate-700">
                    {resolveProductSummary(activeBook, t) || 'No description available.'}
                  </p>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <p className="text-lg font-extrabold text-orange-600">
                      {(activeBook.currency ?? 'RWF').toUpperCase()} {new Intl.NumberFormat('en-US').format(activeBook.price)}
                    </p>
                    <button
                      type="button"
                      onClick={() => handleAddToCart(activeBook)}
                      className="rounded-2xl bg-gradient-to-br from-orange-600 to-orange-500 px-5 py-3 text-sm font-extrabold text-white shadow-sm transition hover:scale-[1.01]"
                    >
                      {t('assistant.books.addToCart', { defaultValue: 'Add to cart' })}
                    </button>
                  </div>
                </article>
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === 'games' ? (
          <div className="flex h-[480px] flex-col">
            <div className="p-4">
              <PanelTitle icon={Gamepad2} title={t('assistant.games.title', { defaultValue: 'Game Zone' })} />

              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                  {t('assistant.games.difficulty', { defaultValue: 'Difficulty' })}
                </p>
                <div className="flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 p-1">
                  {([
                    { id: 'easy' as const, label: t('assistant.games.difficultyEasy', { defaultValue: 'Easy' }) },
                    { id: 'medium' as const, label: t('assistant.games.difficultyMedium', { defaultValue: 'Medium' }) },
                    { id: 'hard' as const, label: t('assistant.games.difficultyHard', { defaultValue: 'Hard' }) },
                  ] as const).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setDifficulty(item.id)}
                      className={`rounded-full px-3 py-1 text-[11px] font-extrabold transition ${
                        difficulty === item.id
                          ? 'bg-orange-600 text-white shadow-sm'
                          : 'text-orange-700 hover:bg-white'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-[11px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                  {t('assistant.games.game', { defaultValue: 'Game' })}
                </label>
                <select
                  value={game}
                  onChange={(event) => setGame(event.target.value as GameId)}
                  className="mt-2 w-full rounded-2xl border border-orange-200 bg-[#fff8f2] px-4 py-3 text-sm font-extrabold text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200/60"
                >
                  <option value="quiz">{t('assistant.games.options.quiz', { defaultValue: 'Quiz' })}</option>
                  <option value="scramble">{t('assistant.games.options.scramble', { defaultValue: 'Scramble' })}</option>
                  <option value="memory">{t('assistant.games.options.memory', { defaultValue: 'Memory' })}</option>
                  <option value="flappy">{t('assistant.games.options.flappy', { defaultValue: 'Flappy Book' })}</option>
                  <option value="block">{t('assistant.games.options.block', { defaultValue: 'Block Jump' })}</option>
                  <option value="dino">{t('assistant.games.options.dino', { defaultValue: 'Dino Run' })}</option>
                  <option value="snake">{t('assistant.games.options.snake', { defaultValue: 'Snake' })}</option>
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {game === 'quiz' ? (
                <article className="rounded-3xl border border-orange-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-extrabold text-orange-700">
                      {isQuizDone ? 'Finished' : `Question ${quizIndex + 1}/${quizQuestions.length}`}
                    </p>
                    <p className="text-xs font-extrabold text-slate-700">Score: {quizScore}</p>
                  </div>

                  {isQuizDone ? (
                    <div className="py-10 text-center">
                      <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-700">Quiz complete</p>
                      <p className="mt-2 text-lg font-extrabold text-slate-900">
                        {quizScore}/{quizQuestions.length}
                      </p>
                      <button
                        type="button"
                        onClick={resetQuiz}
                        className="mt-5 rounded-2xl bg-orange-600 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-orange-700"
                      >
                        Play again
                      </button>
                    </div>
                  ) : quizQuestion ? (
                    <>
                      <p className="mt-4 text-sm font-extrabold text-slate-900">{quizQuestion.q}</p>
                      <div className="mt-4 space-y-2">
                        {quizQuestion.options.map((option, index) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => answerQuiz(index)}
                            disabled={quizAnswered}
                            className="w-full rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-left text-sm font-bold text-slate-900 transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-70"
                          >
                            {String.fromCharCode(65 + index)}. {option}
                          </button>
                        ))}
                      </div>
                      <p className="mt-3 text-xs font-semibold text-slate-500">Tip: answer fast and try to get a perfect score.</p>
                    </>
                  ) : null}
                </article>
              ) : null}

              {game === 'flappy' || game === 'block' || game === 'dino' || game === 'snake' ? (
                <div className="space-y-3">
                  <AssistantCanvasGames
                    game={game as AssistantArcadeGame}
                    difficulty={difficulty}
                    canvasHeight={160}
                    onRequestFullscreen={() => setFullscreenGame(game as AssistantArcadeGame)}
                  />
                  <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-xs font-semibold text-slate-700">
                    {t('assistant.games.tip', {
                      defaultValue: 'Tip: use {{mode}} mode to control speed and challenge.',
                      mode:
                        difficulty === 'easy'
                          ? t('assistant.games.difficultyEasy', { defaultValue: 'Easy' })
                          : difficulty === 'medium'
                            ? t('assistant.games.difficultyMedium', { defaultValue: 'Medium' })
                            : t('assistant.games.difficultyHard', { defaultValue: 'Hard' }),
                    })}
                  </div>
                </div>
              ) : null}

              {game === 'scramble' ? (
                <article className="rounded-3xl border border-orange-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-extrabold text-orange-700">
                      {isScrambleDone ? 'Finished' : `Word ${scrambleIndex + 1}/${scrambleWords.length}`}
                    </p>
                    <p className="text-xs font-extrabold text-slate-700">Score: {scrambleScore}</p>
                  </div>

                  {isScrambleDone ? (
                    <div className="py-10 text-center">
                      <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-orange-700">Scramble complete</p>
                      <p className="mt-2 text-lg font-extrabold text-slate-900">
                        {scrambleScore}/{scrambleWords.length}
                      </p>
                      <button
                        type="button"
                        onClick={resetScramble}
                        className="mt-5 rounded-2xl bg-orange-600 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-orange-700"
                      >
                        Play again
                      </button>
                    </div>
                  ) : scrambleEntry ? (
                    <>
                      <p className="mt-4 text-xs font-semibold text-slate-500">{scrambleEntry.hint}</p>
                      <p className="mt-3 rounded-2xl bg-orange-50 px-4 py-3 text-center text-2xl font-extrabold tracking-[0.25em] text-orange-600">
                        {scrambledWord}
                      </p>
                      <input
                        value={scrambleGuess}
                        onChange={(event) => {
                          setScrambleGuess(event.target.value);
                          if (scrambleResult !== 'idle') setScrambleResult('idle');
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') checkScramble();
                        }}
                        placeholder="Type your answer..."
                        className="mt-4 w-full rounded-2xl border border-orange-200 bg-[#fff8f2] px-4 py-3 text-center text-sm font-extrabold text-slate-900 outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200/60"
                      />
                      <p
                        className={`mt-3 min-h-[20px] text-center text-sm font-extrabold ${
                          scrambleResult === 'ok'
                            ? 'text-emerald-600'
                            : scrambleResult === 'no'
                              ? 'text-rose-600'
                              : 'text-slate-400'
                        }`}
                      >
                        {scrambleResult === 'ok' ? 'Correct!' : scrambleResult === 'no' ? 'Try again.' : ''}
                      </p>
                      <div className="mt-2 flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={checkScramble}
                          className="rounded-2xl bg-orange-600 px-4 py-2.5 text-sm font-extrabold text-white transition hover:bg-orange-700"
                        >
                          Check
                        </button>
                        <button
                          type="button"
                          onClick={skipScramble}
                          className="rounded-2xl border border-orange-200 bg-white px-4 py-2.5 text-sm font-extrabold text-orange-700 transition hover:bg-orange-50"
                        >
                          Skip
                        </button>
                      </div>
                    </>
                  ) : null}
                </article>
              ) : null}

              {game === 'memory' ? (
                <article className="rounded-3xl border border-orange-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-extrabold text-orange-700">Memory Match</p>
                    <p className="text-xs font-extrabold text-slate-700">
                      Pairs: {memoryMatched.length / 2}/{memoryPairsCount} - Time: {memorySeconds}s - Moves: {memoryMoves}
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {memoryCards.map((card, index) => {
                      const isFlipped = memoryFlipped.includes(index) || memoryMatched.includes(index);
                      const isMatched = memoryMatched.includes(index);
                      return (
                        <button
                          key={`${card}-${index}`}
                          type="button"
                          onClick={() => flipMemory(index)}
                          disabled={isMatched || memoryLocked}
                          className={`aspect-square rounded-2xl border text-xl font-extrabold shadow-sm transition ${
                            isFlipped
                              ? 'border-orange-200 bg-white'
                              : 'border-orange-300 bg-gradient-to-br from-orange-600 to-orange-500 text-transparent'
                          } ${isMatched ? 'opacity-60' : ''}`}
                        >
                          {isFlipped ? card : '*'}
                        </button>
                      );
                    })}
                  </div>

                  {memoryMatched.length === memoryCards.length ? (
                    <div className="mt-4 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-center">
                      <p className="text-lg font-extrabold text-slate-900">You won!</p>
                      <p className="mt-1 text-sm font-semibold text-slate-600">
                        Time: {memorySeconds}s - Moves: {memoryMoves}
                      </p>
                      <button
                        type="button"
                        onClick={resetMemory}
                        className="mt-3 rounded-2xl bg-orange-600 px-5 py-3 text-sm font-extrabold text-white transition hover:bg-orange-700"
                      >
                        Play again
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={resetMemory}
                      className="mt-4 w-full rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-extrabold text-orange-700 transition hover:bg-orange-50"
                    >
                      Restart
                    </button>
                  )}
                </article>
              ) : null}

              <p className="mt-4 text-center text-[11px] font-semibold text-slate-500">
                More games can be added later (Flappy / Block Jump / Dino / Snake).
              </p>
            </div>
          </div>
        ) : null}
      </section>
    </>
  );
}
